<template>
  <!-- Purchase Success Modal -->
  <PurchaseCompletedModal
    v-model="showPurchasedModal"
    :product-name="purchasedProductName"
    :benefit-type="purchasedBenefitType"
  />

  <!-- Waiting for Purchase Processing -->
  <DialogRoot v-model="isAwaitingPurchase" class="space-y-2">
    <template #body="bodyProps">
      <DialogBody
        v-bind="bodyProps"
        class="!max-w-full !max-h-full !h-full !rounded-none flex flex-col justify-center items-center"
        @close="bodyProps.close"
      >
        <template #actions>
          <div></div>
        </template>
        <div class="flex flex-col max-w-3xl mx-auto">
          <WaitingForMembership />
        </div>
      </DialogBody>
    </template>
  </DialogRoot>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineAsyncComponent } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useChannelStore } from "@/stores/channel.store";
import { useCapabilitiesStore } from "@/stores/capabilities.store";
import { setupPurchaseService } from "@/services/purchase/purchase.service";
import { useReferralService } from "@/features/channel/services/use-referral-service";
import { ConversionType } from "@/generated/graphql";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import { nextEventLoopTick } from "@/utils/reactivity";

import DialogRoot from "@/components/ui/dialog/dialog-root.vue";
import DialogBody from "@/components/ui/dialog/dialog-body.vue";
import WaitingForMembership from "@/components/channel/auth/common/onboarding/steps/waiting-for-membership.vue";

const PurchaseCompletedModal = defineAsyncComponent(
  () => import("@/components/checkout/purchase-completed-modal.vue"),
);

const emit = defineEmits<{
  "purchase-completed": [];
}>();

const route = useRoute();
const router = useRouter();
const channelStore = useChannelStore();
const capabilitiesStore = useCapabilitiesStore();
const membershipService = computed(() => capabilitiesStore.membershipService);
const { emitChannelEvent } = useChannelEvents();
const { processReferralConversion } = useReferralService();

const showPurchasedModal = ref(false);
const isAwaitingPurchase = ref(false);
const purchasedProductName = ref("");
const purchasedBenefitType = ref("generic");

// Watch for payment_intent in URL (Stripe redirect)
watch(
  [
    () => route.query,
    () => channelStore.channel,
    () => membershipService.value,
  ],
  async ([query, channel, membershipServiceValue]) => {
    const { payment_intent } = query;

    if (payment_intent && channel?.id) {
      isAwaitingPurchase.value = true;
      capabilitiesStore.isUpgrading = true;

      try {
        if (!membershipServiceValue) {
          console.log("[PurchaseCompletionHandler] Service not initialized");
          return;
        }

        const pollResult =
          await membershipServiceValue.pollMembershipPurchaseFinished(
            channel.id,
            payment_intent as string,
          );

        if (pollResult) {
          isAwaitingPurchase.value = false;
          capabilitiesStore.isUpgrading = false;

          // Fetch latest purchase to get product info
          try {
            const purchaseService = setupPurchaseService();
            const purchases = await purchaseService.fetchUserPurchases();
            const sorted = [...purchases].sort(
              (a, b) =>
                new Date(b.purchased_at).getTime() -
                new Date(a.purchased_at).getTime(),
            );
            const latest = sorted[0];

            if (latest) {
              purchasedProductName.value = latest.product.name;
              purchasedBenefitType.value =
                latest.product.benefits?.[0]?.benefit_type ?? "generic";
            } else {
              purchasedProductName.value = "Purchase";
              purchasedBenefitType.value = "generic";
            }
          } catch {
            purchasedProductName.value = "Purchase";
            purchasedBenefitType.value = "generic";
          }

          showPurchasedModal.value = true;

          await channelStore.refreshUserInChannel();

          router.replace({
            path: route.path,
            query: {},
          });

          setTimeout(() => {
            showPurchasedModal.value = false;

            nextEventLoopTick().then(() => {
              processReferralConversion(ConversionType.Paid);
              emitChannelEvent({
                type: ChannelEventType.UPGRADE_FINISHED,
                conversionType: ConversionType.Paid,
              });
              emit("purchase-completed");
            });
          }, 3000);
        }
      } catch {
        isAwaitingPurchase.value = false;
      }
    }
  },
  { immediate: true },
);
</script>
